"use client";

import { useCallback, useEffect, useRef } from "react";
import { trackEvent } from "./engageXClient";

/**
 * The lead-form funnel from the tracking plan, for one form:
 *
 *   form_open           the modal opens (or an inline form comes into view)
 *   form_start          first field touched
 *   form_country_select the phone country code changed     { country_code }
 *   form_close_empty    closed with nothing touched         { time_open_seconds, close_method }
 *   form_close_partial  closed after partly filling         { last_field, fields_filled_count, time_open_seconds, close_method }
 *   form_abandon        left the page with an inline form partly filled
 *
 * Usage in a form component:
 *   const ft = useFormTracking({ formName: "enquire", open: isOpen, trigger: "hero" });
 *   <div ref={ft.ref}> ...the fields... </div>
 *   on a successful submit:
 *     trackEvent("enquiry_submitted", { ...existing, ...ft.submitted({ isCorporate }) });
 *
 * For an inline (always shown) form pass inline: true and no `open`.
 * Only field names are recorded, never what was typed.
 */
const fresh = () => ({ openedAt: 0, started: false, touched: new Set(), lastField: null, submitted: false, closeMethod: null, isOpen: false, country: null });

export function useFormTracking({ formName, open, courseName, trigger, inline = false }) {
  const ref = useRef(null);
  const s = useRef(fresh());
  const base = useCallback(
    () => ({ form_name: formName, course_name: courseName || undefined, trigger: trigger || undefined }),
    [formName, courseName, trigger]
  );

  const begin = useCallback(() => {
    s.current = { ...fresh(), country: s.current.country, openedAt: Date.now(), isOpen: true };
    trackEvent("form_open", base());
  }, [base]);

  const finish = useCallback((eventIfPartial, method) => {
    const st = s.current;
    if (!st.isOpen) return;
    st.isOpen = false;
    if (st.submitted) return;
    const secs = Math.round((Date.now() - st.openedAt) / 1000);
    const close = method || st.closeMethod || "outside_click";
    if (!st.started) {
      if (eventIfPartial === "form_abandon") return; // an untouched inline form is not an abandon
      trackEvent("form_close_empty", { ...base(), time_open_seconds: secs, close_method: close });
      return;
    }
    trackEvent(eventIfPartial, {
      ...base(),
      last_field: st.lastField || undefined,
      fields_filled_count: st.touched.size,
      time_open_seconds: secs,
      ...(eventIfPartial === "form_close_partial" ? { close_method: close } : {}),
    });
  }, [base]);

  // Modal forms: open / close follow the `open` prop.
  useEffect(() => {
    if (inline) return;
    if (open && !s.current.isOpen) begin();
    if (!open && s.current.isOpen) finish("form_close_partial");
  }, [open, inline, begin, finish]);

  // A modal unmounted while open (page change) counts as closed.
  useEffect(() => () => { if (!inline) finish("form_close_partial", "navigation"); }, [inline, finish]);

  // Inline forms: "open" when half of it is on screen; abandon when the page is left.
  useEffect(() => {
    if (!inline || !ref.current || typeof IntersectionObserver !== "function") return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !s.current.isOpen && !s.current.submitted) {
        begin();
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(ref.current);
    const leave = () => finish("form_abandon");
    window.addEventListener("pagehide", leave);
    return () => {
      io.disconnect();
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [inline, begin, finish]);

  // Field activity, country changes and how a modal was closed. Listened for on
  // the document so a modal that mounts later (portals, animations) is covered.
  useEffect(() => {
    const inside = (t) => !!(ref.current && t && ref.current.contains(t));
    const onField = (e) => {
      const t = e.target;
      if (!inside(t) || !/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.type === "hidden") return;
      const st = s.current;
      if (!st.isOpen && inline && !st.submitted) begin();
      const field = t.getAttribute("name") || t.getAttribute("aria-label") || t.type || "field";
      st.lastField = String(field).replace(/react-aria\S*/i, t.type || "field").slice(0, 40);
      if (e.type === "focusin") return;
      st.touched.add(st.lastField);
      if (!st.started) {
        st.started = true;
        trackEvent("form_start", base());
      }
    };
    const onCountry = (e) => {
      if (!inside(e.target)) return;
      s.current.country = e.detail?.country || null;
      trackEvent("form_country_select", { form_name: formName, country_code: e.detail?.country_code });
    };
    const onKey = (e) => { if (e.key === "Escape") s.current.closeMethod = "esc"; };
    const onDown = (e) => {
      if (inline || !s.current.isOpen) return;
      const btn = e.target?.closest?.("button");
      const label = (btn?.getAttribute("aria-label") || "").toLowerCase();
      if (btn && (label.includes("close") || /^\s*[×x✕]\s*$/i.test(btn.textContent || ""))) s.current.closeMethod = "x_button";
      else if (!inside(e.target)) s.current.closeMethod = "outside_click";
      else s.current.closeMethod = null;
    };
    document.addEventListener("input", onField, true);
    document.addEventListener("change", onField, true);
    document.addEventListener("focusin", onField, true);
    document.addEventListener("lp-country-select", onCountry, true);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("input", onField, true);
      document.removeEventListener("change", onField, true);
      document.removeEventListener("focusin", onField, true);
      document.removeEventListener("lp-country-select", onCountry, true);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [base, begin, inline, formName]);

  /** Marks the form as sent (no close/abandon event after it) and returns the
   *  plan's generate_lead parameters to add to the submit event. */
  const submitted = useCallback(({ isCorporate, country } = {}) => {
    s.current.submitted = true;
    return {
      form_name: formName,
      trigger: trigger || undefined,
      lead_type: isCorporate || formName === "corporate" ? "corporate" : "individual",
      country: country || s.current.country || undefined,
    };
  }, [formName, trigger]);

  return { ref, submitted };
}
